// /////////////////////////////////////////////////////////////
// [ファイル] ActivityHistoryPanel.tsx
// [概要] 案件の活動履歴（推進案件機能統合 リリース1）
//        旧「案件詳細」と同じく、詳細画面の右の列（担当チームの下）に常に表示するカード。
//        見出し右の「＋」で追加、各履歴の右上のメニューから編集・削除する。
//        テーブル（trn_sfa_activityhistory）と API（get_activity_history / create_activity /
//        update_activity_history / delete_activity_history）は旧画面と同じで、両画面から同じ履歴が見える。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import { ep } from '../../api/endpoints';
import { useMasterData } from '../../providers/MasterDataProvider';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';

interface Activity {
  auto_number: number;
  activity_name: string;
  due_date: string | null;
  assign_to: string | null;
  assign_to_id: string | null;
  description: string | null;
  activity_content: string | null;
  updated_date?: string | null;
}

interface ActivityForm {
  activity_name: string; due_date: string; assign_to_id: string; assign_to: string; description: string; activity_content: string;
}

const emptyForm = (): ActivityForm => ({ activity_name: '', due_date: format(new Date(), 'yyyy-MM-dd'), assign_to_id: '', assign_to: '', description: '', activity_content: '' });
const dialogTitleSx = { fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 } as const;
const wrapSx = { overflowWrap: 'anywhere', wordBreak: 'break-word' } as const;

export default function ActivityHistoryPanel({ projectId, defaultStaff }: { projectId: string; defaultStaff?: { id: string; name: string } | null }) {
  const notify = useNotification();
  // 通知 API は参照が変わるため load の依存に入れない（無限ループ防止）
  const notifyRef = useRef(notify); notifyRef.current = notify;
  const confirm = useConfirm();
  const { staffList } = useMasterData();
  const [rows, setRows] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<{ open: boolean; editing: Activity | null }>({ open: false, editing: null });
  const [f, setF] = useState<ActivityForm>(emptyForm());
  // 各履歴の︙メニュー（編集・削除）
  const [menu, setMenu] = useState<{ anchor: HTMLElement | null; target: Activity | null }>({ anchor: null, target: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(ep('get_activity_history'), { projectId });
      setRows(res.data?.status === 'success' ? (res.data.activities || []) : []);
    } catch (e) {
      console.error(e);
      notifyRef.current.error('活動履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (a: Activity | null) => {
    setMenu({ anchor: null, target: null });
    setF(a ? {
      activity_name: a.activity_name || '', due_date: a.due_date || format(new Date(), 'yyyy-MM-dd'), assign_to_id: a.assign_to_id || '', assign_to: a.assign_to || '',
      description: a.description || '', activity_content: a.activity_content || '',
    } : { ...emptyForm(), assign_to_id: defaultStaff?.id || '', assign_to: defaultStaff?.name || '' });
    setDialog({ open: true, editing: a });
  };

  const save = async () => {
    if (!f.activity_name.trim()) { notify.warning('件名は必須です'); return; }
    setBusy(true);
    try {
      if (dialog.editing) await axios.post(ep('update_activity_history'), { auto_number: dialog.editing.auto_number, activityData: f });
      else await axios.post(ep('create_activity'), { ...f, project_id: projectId });
      notify.success(dialog.editing ? '活動履歴を更新しました' : '活動履歴を追加しました');
      setDialog({ open: false, editing: null });
      load();
    } catch (e) {
      console.error(e);
      notify.error('保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: Activity) => {
    setMenu({ anchor: null, target: null });
    const ok = await confirm({ title: '活動履歴の削除', message: `「${a.activity_name}」を削除してもよろしいですか？`, confirmText: '削除する', destructive: true });
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(ep('delete_activity_history'), { auto_number: a.auto_number });
      notify.success('削除しました');
      load();
    } catch (e) {
      console.error(e);
      notify.error('削除に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const staffValue = staffList.find((s: any) => s.staff_id === f.assign_to_id) || staffList.find((s: any) => s.staff_name === f.assign_to) || null;

  return (
    <Paper sx={{ p: 1.5, minWidth: 0 }}>
      {/* 見出し（旧画面と同じく右端の「＋」で追加） */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ bgcolor: 'primary.light', color: 'white', width: 22, height: 22, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icons.Task sx={{ fontSize: 16 }} />
        </Box>
        <Typography variant="subtitle2">活動履歴</Typography>
        {rows.length > 0 && <Typography variant="caption" color="text.secondary">{rows.length} 件</Typography>}
        <Tooltip title="活動を追加">
          <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
            <IconButton
              size="small" onClick={() => openDialog(null)} disabled={busy}
              sx={{ width: 23, height: 23, bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
            >
              <Icons.Add fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {busy && <LoadingView message="処理中..." height="50px" size={24} />}
      {loading ? (
        <LoadingView message="読み込み中..." height="120px" size={30} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>活動履歴がありません。</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((a) => (
            <Card key={a.auto_number} variant="outlined" sx={{ borderRadius: 1, minWidth: 0 }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                {/* 上部: 件名と期日、操作メニュー */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, flex: 1, minWidth: 0, ...wrapSx }}>{a.activity_name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mt: 0.3 }}>期日 {a.due_date ? a.due_date.replace(/-/g, '/') : '-'}</Typography>
                  <IconButton size="small" disabled={busy} onClick={(e) => setMenu({ anchor: e.currentTarget, target: a })} sx={{ mt: -0.5, mr: -0.75 }}>
                    <Icons.MoreVert fontSize="small" />
                  </IconButton>
                </Box>
                {/* 下部: 活動内容・備考・担当 */}
                {a.activity_content && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', ...wrapSx }}>{a.activity_content}</Typography>}
                {a.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', whiteSpace: 'pre-wrap', ...wrapSx }}>
                    <Chip label="備 考" sx={{ mr: 1, bgcolor: blueGrey[200], color: 'white', fontSize: '10px', borderRadius: 1, height: 18 }} />{a.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ...wrapSx }}>担当: {a.assign_to || '-'}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* 各履歴の操作メニュー（旧画面と同じ） */}
      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={() => setMenu({ anchor: null, target: null })} disableScrollLock>
        <MenuItem onClick={() => openDialog(menu.target)}><Icons.Edit sx={{ fontSize: 18, mr: 1 }} /> 編集</MenuItem>
        <MenuItem onClick={() => menu.target && remove(menu.target)} sx={{ color: 'error.main' }}><Icons.Delete sx={{ fontSize: 18, mr: 1 }} /> 削除</MenuItem>
      </Menu>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, editing: null })} fullWidth maxWidth="sm" disableScrollLock>
        <DialogTitle sx={dialogTitleSx}><Icons.Task sx={{ fontSize: 18 }} />{dialog.editing ? '活動履歴の編集' : '活動履歴の追加'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField size="small" label="件名" required value={f.activity_name} onChange={(e) => setF({ ...f, activity_name: e.target.value })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField size="small" type="date" label="期日" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
              <Autocomplete
                size="small" options={staffList} getOptionLabel={(o: any) => o.staff_name || ''} value={staffValue}
                isOptionEqualToValue={(o: any, v: any) => o.staff_id === v.staff_id}
                onChange={(_, v: any) => setF({ ...f, assign_to: v ? v.staff_name : '', assign_to_id: v ? v.staff_id : '' })}
                renderInput={(params) => <TextField {...params} label="担当者" size="small" />}
              />
            </Box>
            <TextField size="small" label="活動内容" multiline minRows={4} value={f.activity_content} onChange={(e) => setF({ ...f, activity_content: e.target.value })} />
            <TextField size="small" label="備考" multiline minRows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, editing: null })} sx={STANDARD_TEXT_BUTTON_SX} disabled={busy}>キャンセル</Button>
          <Button onClick={save} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} disabled={busy || !f.activity_name.trim()}>保存する</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
